import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  PointOfSale as PosIcon,
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const currencyRD = (v) =>
  new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(v);

export default function AbrirTurnoDialog({ open, onClose, onShiftOpened }) {
  const [initialCash, setInitialCash] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!initialCash || parseFloat(initialCash) < 0) {
      setError('Por favor ingrese un monto válido para el fondo inicial');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const shiftData = {
        idcompany: localStorage.getItem('idcompany'),
        id_sucursal: localStorage.getItem('id_sucursal'),
        id_usuario: localStorage.getItem('id'),
        monto_inicial: parseFloat(initialCash),
        fecha_apertura: new Date().toISOString().slice(0, 19).replace('T', ' '),
        estado: 'abierto'
      };

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/pos/shifts/open`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify(shiftData)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          onShiftOpened(result.shift_id, parseFloat(initialCash));
          handleClose();
        }, 1500);
      } else {
        setError(result.message || 'Error al abrir el turno');
      }
    } catch (error) {
      console.error('Error opening shift:', error);
      setError('Error de conexión. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setInitialCash('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading) {
      handleSubmit();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 400
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <PosIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Abrir Turno de Caja
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure el fondo inicial para comenzar las ventas
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" color="success.main" gutterBottom>
              ¡Turno Abierto Exitosamente!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fondo inicial: {currencyRD(initialCash)}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" icon={<ErrorIcon />}>
                {error}
              </Alert>
            )}

            <Box sx={{ 
              bgcolor: 'grey.50', 
              p: 3, 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <WalletIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Información del Turno
                </Typography>
              </Stack>
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Usuario
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {localStorage.getItem('nombre') || 'Usuario'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Sucursal
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {localStorage.getItem('nombre_sucursal') || 'Sucursal'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Almacén
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {localStorage.getItem('nombre_almacen') || 'Almacén'}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Fondo Inicial de Caja
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ingrese el monto de efectivo disponible en la caja para comenzar el turno
              </Typography>
              <Typography fontWeight={600} color="text.secondary" sx={{ mb: 2 }}>Monto inicial (RD$)</Typography>
              <TextField
                fullWidth
                placeholder="0.00"
                type="number"
                value={initialCash}
                onChange={(e) => setInitialCash(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>RD$</Typography>,
                  inputProps: { 
                    min: 0,
                    step: 0.01
                  }
                }}
                disabled={loading}
                sx={{
                  marginTop: -2,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                    fontWeight: 500
                  }
                }}
              />
              
              {initialCash && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Monto ingresado: {currencyRD(parseFloat(initialCash) || 0)}
                </Typography>
              )}
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Importante:</strong> Este monto será el fondo inicial de su turno. 
                Asegúrese de contar el efectivo disponible antes de continuar.
              </Typography>
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        {!success && (
          <>
            <Button 
              onClick={handleClose} 
              disabled={loading}
              sx={{ minWidth: 100 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primarybutton"
              onClick={handleSubmit}
              disabled={loading || !initialCash}
              startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
              sx={{ 
                minWidth: 120,
                fontWeight: 600
              }}
            >
              {loading ? 'Abriendo...' : 'Abrir Turno'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
